<template>
  <div class="comment-composer-mentions-root">
    <div class="comment-editable-wrap">
      <div class="comment-editable-label caption grey--text">{{ label }}</div>
      <div
        ref="comentarioEditable"
        class="comment-editable"
        contenteditable="true"
        tabindex="0"
        :data-placeholder="placeholder"
        @input="onComentarioEditableInput"
        @keydown="onComentarioEditableKeydown"
        @paste="onComentarioEditablePaste"
      />
    </div>

    <v-menu
      v-model="mentionMenu.show"
      :position-x="mentionMenu.x"
      :position-y="mentionMenu.y"
      absolute
      offset-y
      :close-on-content-click="true"
    >
      <v-list dense max-height="280" class="mention-menu-list">
        <template v-if="usuariosMencionFiltrados.length === 0">
          <v-list-item disabled>
            <v-list-item-content>
              <v-list-item-title class="caption">Sin coincidencias</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
        <template v-else>
          <v-list-item
            v-for="(u, idx) in usuariosMencionFiltrados"
            :key="'m-' + u.Usuario_id"
            :class="{ 'mention-menu-item--active': idx === mentionFiltroIndice }"
            @click="insertarMencionUsuario(u)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ u.Apellido }}, {{ u.Nombre }}</v-list-item-title>
              <v-list-item-subtitle class="caption">@{{ u.Username }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { mapState } from 'vuex'

const DEFAULT_ROLES = [1, 50, 54, 56]

export default {
  name: 'CommentComposerMentions',

  props: {
    /** Texto plano del comentario (v-model) */
    value: {
      type: String,
      default: '',
    },
    /** 'todos' o array de Rol_id (ej. [1, 50, 54, 56]) */
    roles: {
      type: [Array, String],
      default: () => [...DEFAULT_ROLES],
    },
    /**
     * Clave de perfil SMTP en servidor (resolveSmtpProfile / SMTP_MENTION_ALLOWED_PROFILES).
     * El padre debe enviarla en POST nuevo-comentario como smtp_profile.
     */
    smtpProfile: {
      type: String,
      default: 'default',
    },
    label: {
      type: String,
      default: 'Comentario…',
    },
    placeholder: {
      type: String,
      default: 'Escriba aquí. Use @ para mencionar a un usuario.',
    },
  },

  data() {
    return {
      mentionMenu: { show: false, x: 0, y: 0 },
      mentionQuery: '',
      mentionFiltroIndice: 0,
    }
  },

  computed: {
    ...mapState('postventaTickets', ['usuariosMenciones']),

    rolesPayload() {
      const r = this.roles
      if (r === 'todos' || (typeof r === 'string' && r.trim().toLowerCase() === 'todos')) {
        return 'todos'
      }
      if (Array.isArray(r) && r.length > 0) {
        return r
      }
      return [...DEFAULT_ROLES]
    },

    usuariosMencionParaTokenizar() {
      const list = [...(this.usuariosMenciones || [])]
      const seen = new Set()
      const uniq = []
      for (const u of list) {
        if (!u || !u.Username || seen.has(u.Username)) continue
        seen.add(u.Username)
        uniq.push(u)
      }
      uniq.sort((a, b) => (b.Username || '').length - (a.Username || '').length)
      return uniq
    },

    usuariosMencionFiltrados() {
      const list = this.usuariosMenciones || []
      const raw = (this.mentionQuery || '').trim()
      const q = raw
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
      const matches = (s) => {
        const t = String(s || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
        return !q || t.includes(q)
      }
      return list
        .filter((u) => matches(u.Username) || matches(u.Nombre) || matches(u.Apellido))
        .slice(0, 25)
    },
  },

  watch: {
    roles: {
      deep: true,
      handler() {
        this.loadUsuariosMenciones()
      },
    },
    value(val) {
      this.$nextTick(() => {
        const el = this.$refs.comentarioEditable
        if (!el) return
        if (!val || !String(val).trim()) {
          if (el.innerText && el.innerText.trim()) el.innerHTML = ''
        }
      })
    },
  },

  mounted() {
    this.loadUsuariosMenciones()
  },

  methods: {
    loadUsuariosMenciones() {
      const r = this.rolesPayload
      return this.$store.dispatch('postventaTickets/getUsuariosMenciones', {
        roles: r,
      })
    },

    escapeHtml(s) {
      return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },

    escapeHtmlAttr(s) {
      return this.escapeHtml(s).replace(/'/g, '&#39;')
    },

    getTextBeforeCaret(rootEl) {
      const sel = window.getSelection()
      if (!sel.rangeCount || !rootEl.contains(sel.anchorNode)) return ''
      const r = document.createRange()
      r.selectNodeContents(rootEl)
      r.setEnd(sel.anchorNode, sel.anchorOffset)
      return r.toString()
    },

    setCaretAtPlainTextOffset(root, offset) {
      const sel = window.getSelection()
      const range = document.createRange()
      let count = 0
      const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const len = node.length
          if (count + len >= offset) {
            range.setStart(node, Math.min(Math.max(0, offset - count), len))
            range.collapse(true)
            return true
          }
          count += len
          return false
        }
        if (node.nodeName === 'BR') {
          if (count + 1 >= offset) {
            range.setStartBefore(node)
            range.collapse(true)
            return true
          }
          count += 1
          return false
        }
        if (node.nodeType === Node.ELEMENT_NODE && node.childNodes) {
          for (let i = 0; i < node.childNodes.length; i++) {
            if (walk(node.childNodes[i])) return true
          }
        }
        return false
      }
      if (!walk(root)) {
        range.selectNodeContents(root)
        range.collapse(false)
      }
      sel.removeAllRanges()
      sel.addRange(range)
    },

    isMentionBoundaryAt(str, idx) {
      if (idx >= str.length) return true
      const c = str[idx]
      return /\s/.test(c) || c === '\n'
    },

    plainTextToMentionHtml(text) {
      if (!text) return ''
      const users = this.usuariosMencionParaTokenizar
      let i = 0
      let out = ''
      while (i < text.length) {
        const ch = text[i]
        if (ch === '@') {
          let matched = null
          for (const u of users) {
            const un = u.Username
            if (!un) continue
            const token = `@${un}`
            if (text.startsWith(token, i) && this.isMentionBoundaryAt(text, i + token.length)) {
              matched = u
              break
            }
          }
          if (matched) {
            const emailAttr = this.escapeHtmlAttr(String(matched.Email || '').trim())
            const unEsc = this.escapeHtml(matched.Username)
            const unAttr = this.escapeHtmlAttr(matched.Username)
            out += `<span class="mention accent--text" contenteditable="false" data-email="${emailAttr}" data-username="${unAttr}">@${unEsc}</span>`
            i += 1 + matched.Username.length
            continue
          }
        }
        if (ch === '\n') {
          out += '<br>'
          i++
          continue
        }
        out += this.escapeHtml(ch)
        i++
      }
      return out
    },

    actualizarMenuMenciones(el) {
      const sel = window.getSelection()
      if (!sel.rangeCount || !el.contains(sel.anchorNode)) {
        this.mentionMenu.show = false
        return
      }
      const before = this.getTextBeforeCaret(el)
      const m = before.match(/@([^\s@\n]*)$/)
      if (!m) {
        this.mentionMenu.show = false
        return
      }
      this.mentionQuery = m[1] || ''
      this.mentionFiltroIndice = 0
      const rect = sel.getRangeAt(0).getBoundingClientRect()
      this.mentionMenu.x = rect.left
      this.mentionMenu.y = rect.bottom + 4
      this.mentionMenu.show = true
    },

    onComentarioEditableInput() {
      const el = this.$refs.comentarioEditable
      if (!el) return
      const t = el.innerText || ''
      this.$emit('input', t)
      this.actualizarMenuMenciones(el)
    },

    onComentarioEditableKeydown(e) {
      if (!this.mentionMenu.show) return
      const list = this.usuariosMencionFiltrados
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (list.length === 0) return
        this.mentionFiltroIndice = Math.min(this.mentionFiltroIndice + 1, list.length - 1)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.mentionFiltroIndice = Math.max(this.mentionFiltroIndice - 1, 0)
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        const u = list[this.mentionFiltroIndice]
        if (u) this.insertarMencionUsuario(u)
      } else if (e.key === 'Escape') {
        this.mentionMenu.show = false
      }
    },

    onComentarioEditablePaste(e) {
      e.preventDefault()
      const text = (e.clipboardData || window.clipboardData).getData('text/plain')
      try {
        document.execCommand('insertText', false, text)
      } catch {
        const el = this.$refs.comentarioEditable
        const sel = window.getSelection()
        if (!el || !sel.rangeCount) return
        const r = sel.getRangeAt(0)
        r.deleteContents()
        r.insertNode(document.createTextNode(text))
        r.collapse(false)
        sel.removeAllRanges()
        sel.addRange(r)
      }
      this.onComentarioEditableInput()
    },

    insertarMencionUsuario(u) {
      const el = this.$refs.comentarioEditable
      if (!el || !u || !u.Username) return
      const sel = window.getSelection()
      if (!sel.rangeCount) return
      const r0 = sel.getRangeAt(0)
      const rBefore = document.createRange()
      rBefore.selectNodeContents(el)
      rBefore.setEnd(r0.startContainer, r0.startOffset)
      const before = rBefore.toString()
      const rAfter = document.createRange()
      rAfter.selectNodeContents(el)
      rAfter.setStart(r0.endContainer, r0.endOffset)
      const after = rAfter.toString()
      const m = before.match(/@([^\s@\n]*)$/)
      if (!m) {
        this.mentionMenu.show = false
        return
      }
      const full = before.slice(0, before.length - m[0].length) + `@${u.Username} ` + after
      el.innerHTML = this.plainTextToMentionHtml(full)
      const pos = before.length - m[0].length + 1 + u.Username.length + 1
      this.$nextTick(() => {
        this.setCaretAtPlainTextOffset(el, pos)
        el.focus()
        this.$emit('input', el.innerText || '')
        this.mentionMenu.show = false
      })
    },

    /** Para el padre al enviar el comentario */
    getMencionesEmails() {
      const el = this.$refs.comentarioEditable
      if (!el) return []
      const spans = el.querySelectorAll('.mention[data-email]')
      const seen = new Set()
      const out = []
      spans.forEach((s) => {
        const raw = (s.getAttribute('data-email') || '').trim().toLowerCase()
        if (raw && !seen.has(raw)) {
          seen.add(raw)
          out.push(raw)
        }
      })
      return out
    },

    clear() {
      this.mentionMenu.show = false
      this.$emit('input', '')
      this.$nextTick(() => {
        const el = this.$refs.comentarioEditable
        if (el) el.innerHTML = ''
      })
    },

    focus() {
      this.$refs.comentarioEditable?.focus()
    },
  },
}
</script>

<style scoped>
.comment-composer-mentions-root {
  position: relative;
}

.comment-editable-wrap {
  border: thin solid var(--v-tab-base);
  border-radius: 4px;
  padding: 8px 12px 10px;
  background: transparent;
}

.comment-editable-label {
  margin-bottom: 4px;
  display: block;
}

.comment-editable {
  min-height: 56px;
  max-height: 200px;
  overflow-y: auto;
  outline: none;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.comment-editable:focus {
  outline: none;
}

.comment-editable:empty:before {
  content: attr(data-placeholder);
  color: rgba(0, 0, 0, 0.38);
  pointer-events: none;
}

.theme--dark .comment-editable:empty:before,
.ticket-detail-card--dark .comment-editable:empty:before {
  color: rgba(255, 255, 255, 0.45);
}

.mention-menu-item--active {
  background-color: rgba(25, 118, 210, 0.08);
}
</style>
